import React, { useState, useEffect } from 'react';
import { 
  ShieldCheckIcon, 
  DocumentTextIcon, 
  ExclamationTriangleIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../auth/AuthContext';
import api from '../services/api';

export default function HIPAACompliance() {
  const { user } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [violations, setViolations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPolicy, setUploadingPolicy] = useState(false);

  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    try {
      const [policiesResponse, violationsResponse] = await Promise.all([
        api.get('/health/hipaa/policies'),
        api.get('/health/hipaa/violations')
      ]);
      
      setPolicies(policiesResponse.data);
      setViolations(violationsResponse.data);
    } catch (error) {
      console.error('Failed to load compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingPolicy(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post('/api/v1/health/hipaa/policies/upload', formData);
      await loadComplianceData();
      event.target.value = '';
    } catch (error) {
      console.error('Failed to upload policy:', error);
      alert('Failed to upload policy. Please try again.');
    } finally {
      setUploadingPolicy(false);
    }
  };

  const handleReloadPolicies = async () => {
    setLoading(true);
    try {
      const response = await api.post('/health/hipaa/policies/reload');
      console.log('Policies reloaded:', response.data);
      await loadComplianceData();
      alert(`Successfully reloaded ${response.data.policies_loaded} healthcare policies!`);
    } catch (error) {
      console.error('Failed to reload policies:', error);
      alert('Failed to reload policies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const policyTypes = [
    {
      name: 'HIPAA Privacy Rule',
      description: 'Protects individually identifiable health information',
      icon: ShieldCheckIcon,
      color: 'blue'
    },
    {
      name: 'HIPAA Security Rule',
      description: 'Safeguards for electronic protected health information',
      icon: ShieldCheckIcon,
      color: 'green'
    },
    {
      name: 'Breach Notification Rule',
      description: 'Requirements for breach notification procedures',
      icon: ExclamationTriangleIcon,
      color: 'red'
    },
    {
      name: 'Business Associate Agreements',
      description: 'Contractual protections with third parties',
      icon: UserGroupIcon,
      color: 'purple'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">HIPAA Compliance</h1>
        <p className="mt-2 text-gray-600">
          Manage healthcare privacy policies and monitor compliance violations
        </p>
      </div>

      {/* Policy Upload Section */}
      <div className="bg-white shadow rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Upload HIPAA Policy</h2>
          <button
            onClick={handleReloadPolicies}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Reloading...' : 'Reload Policies'}
          </button>
        </div>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
          <div className="text-center">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4">
              <label className="cursor-pointer">
                <span className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                  {uploadingPolicy ? 'Uploading...' : 'Choose File'}
                </span>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt,.md"
                  onChange={handlePolicyUpload}
                  disabled={uploadingPolicy}
                />
              </label>
            </div>
            <p className="mt-2 text-sm text-gray-500">
              PDF, DOC, DOCX, TXT, or MD up to 10MB
            </p>
          </div>
        </div>
      </div>

      {/* Policy Types Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {policyTypes.map((type) => {
          const Icon = type.icon;
          const typeCount = policies.filter(p => p.type === type.name).length;
          
          return (
            <div key={type.name} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <Icon className={`h-8 w-8 text-${type.color}-500`} />
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${type.color}-100 text-${type.color}-800`}>
                  {typeCount} policies
                </span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{type.name}</h3>
              <p className="text-sm text-gray-500">{type.description}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Policies */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recent Policies</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {policies.slice(0, 5).map((policy) => (
              <div key={policy.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">{policy.name}</h4>
                    <p className="text-sm text-gray-500">{policy.type}</p>
                    <div className="flex items-center mt-1 text-xs text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {new Date(policy.uploaded_at).toLocaleDateString()}
                    </div>
                  </div>
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                </div>
              </div>
            ))}
            {policies.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                No policies uploaded yet
              </div>
            )}
          </div>
        </div>

        {/* Recent Violations */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Compliance Alerts</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {violations.slice(0, 5).map((violation) => (
              <div key={violation.id} className="px-6 py-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900">{violation.type}</h4>
                    <p className="text-sm text-gray-500 mt-1">{violation.description}</p>
                    <div className="flex items-center mt-1 text-xs text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {new Date(violation.detected_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    violation.severity === 'high' 
                      ? 'bg-red-100 text-red-800'
                      : violation.severity === 'medium'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {violation.severity}
                  </span>
                </div>
              </div>
            ))}
            {violations.length === 0 && (
              <div className="px-6 py-8 text-center text-gray-500">
                <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto mb-2" />
                No compliance violations detected
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compliance Metrics */}
      <div className="mt-8 bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">98.5%</div>
            <div className="text-sm text-gray-500">Compliance Score</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{policies.length}</div>
            <div className="text-sm text-gray-500">Active Policies</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600">{violations.length}</div>
            <div className="text-sm text-gray-500">Open Issues</div>
          </div>
        </div>
      </div>
    </div>
  );
}